import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getPendingAppendsByProject,
  getPendingAppendsByRecord,
  type PendingAppend,
  type PendingAppendStatus,
} from "@/offline/db";

/**
 * Query key for a record's queued appends. Kept as a free-standing helper so
 * the modal/grid read the exact same literal the drain and tap-to-retry
 * invalidate by — `["pendingAppends", "byRecord", recordId]`.
 *
 * NOTE: the drain invalidates the `["pendingAppends"]` PREFIX, which refetches
 * this key too. Do not route it through a `query-keys.ts` factory.
 */
export function pendingAppendsByRecordKey(recordId: string) {
  return ["pendingAppends", "byRecord", recordId] as const;
}

/** Query key for a project's queued appends — see {@link pendingAppendsByRecordKey}. */
export function pendingAppendsByProjectKey(projectId: string) {
  return ["pendingAppends", "byProject", projectId] as const;
}

/**
 * A queued append decorated with a ready-to-render preview URL for its
 * locally captured annotated bytes. The open viewer renders these as ORDINARY
 * photo tiles after the record's already-synced photos — no per-photo syncing
 * badge — straight from `previewUrl` (no network, no asset-cache lookup),
 * because the blob is already in hand. See [[append-photo-outbox]].
 */
export interface PendingAppendTile {
  /** `client_photo_uuid` — stable per photo across every upload attempt. */
  photoId: string;
  /** Object URL minted from the locally captured annotated blob. */
  previewUrl: string;
  status: PendingAppendStatus;
}

/** Identity for the object-URL cache. An append's blob never changes once
 * queued (add-only, no edit lifecycle), so the photoId alone is the key. */
function tileCacheKey(append: PendingAppend): string {
  return append.photoId;
}

/**
 * Reads a record's queued appends (pending + failed) as a TanStack query
 * keyed `["pendingAppends", "byRecord", recordId]`, and hands back tiles whose
 * previews are object URLs minted from the locally captured annotated blob.
 *
 * Object-URL lifecycle (mirrors `usePendingRecords`): a URL is minted the
 * first time a given `photoId` is seen and reused on subsequent reads; it is
 * revoked when that append leaves the outbox (synced) and when the hook
 * unmounts. No leaks.
 *
 * Returned newest-LAST: sorted by `createdAt` ascending (capture order), so
 * the appended photos read like the rest of the record's photos — earliest
 * first — rather than the create grid's newest-first.
 *
 * The query is invalidated by the modal's queue-and-close, the drain, and
 * tap-to-retry via the `["pendingAppends"]` prefix.
 */
export function usePendingAppends(recordId: string): PendingAppendTile[] {
  const query = useQuery({
    queryKey: pendingAppendsByRecordKey(recordId),
    queryFn: () => getPendingAppendsByRecord(recordId),
    // The append outbox is local IndexedDB; only mutations (queue/drain/retry)
    // change it and they invalidate this key explicitly. No refetch churn.
    staleTime: Infinity,
    gcTime: 0,
  });

  const appends = query.data;

  // Object-URL cache keyed by `photoId`, held in STATE (not a ref). All
  // minting and revoking happens in the effect below — never during render —
  // so render is a pure read of this map. A URL is created once per photoId
  // and reused; it is revoked when its append leaves the outbox.
  const [urlCache, setUrlCache] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  // Reconcile the URL cache against the current appends: mint a URL for any
  // newly-seen photoId, revoke any that has gone stale. This is the ONLY place
  // object URLs are created or revoked — every URL side effect is kept out of
  // render. The updater is a no-op (`return prev`) whenever nothing changed,
  // so it cannot loop — hence the scoped `set-state-in-effect` disable.
  useEffect(() => {
    if (!appends) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- object-URL lifecycle sync with async query data; guarded no-op when unchanged
    setUrlCache((prev) => {
      const liveKeys = new Set(appends.map(tileCacheKey));
      let changed = false;
      const next = new Map(prev);
      // Revoke + drop any URL whose append left the outbox.
      for (const [key, url] of prev) {
        if (!liveKeys.has(key)) {
          URL.revokeObjectURL(url);
          next.delete(key);
          changed = true;
        }
      }
      // Mint a URL for any append not yet in the cache.
      for (const append of appends) {
        const key = tileCacheKey(append);
        if (!next.has(key)) {
          next.set(key, URL.createObjectURL(append.annotatedBlob));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [appends]);

  // Mirror the latest URL cache into a ref so the unmount cleanup below can
  // revoke every URL WITHOUT calling a state setter on an unmounting hook.
  const urlCacheRef = useRef(urlCache);
  useEffect(() => {
    urlCacheRef.current = urlCache;
  }, [urlCache]);

  // Revoke every outstanding URL when the hook unmounts — no leaks.
  useEffect(() => {
    return () => {
      for (const url of urlCacheRef.current.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  // Pure derivation: build the tiles from `appends` and the state-backed URL
  // cache. An append whose URL has not been minted yet (the effect runs after
  // this render) is skipped — it appears on the very next render once the
  // cache state updates. No ref access, no side effects.
  return useMemo<PendingAppendTile[]>(() => {
    if (!appends) return [];
    // Oldest-first — capture order, so appended photos read like the rest of
    // the record's photos.
    const sorted = [...appends].sort((a, b) => a.createdAt - b.createdAt);
    const tiles: PendingAppendTile[] = [];
    for (const append of sorted) {
      const url = urlCache.get(tileCacheKey(append));
      if (!url) continue;
      tiles.push({
        photoId: append.photoId,
        previewUrl: url,
        status: append.status,
      });
    }
    return tiles;
  }, [appends, urlCache]);
}

/**
 * Per-project append sync state for the photo grid. Reads the SAME outbox via
 * `["pendingAppends", "byProject", projectId]` and returns two record-id sets:
 *
 *  - `failedRecordIds` — a record id appears if ANY of its appends is `failed`.
 *  - `pendingRecordIds` — a record id appears if any of its appends is
 *    `pending` AND none is failed.
 *
 * A record is therefore classified `failed` if even one of its appends
 * permanently failed (the tile shows an error badge), otherwise `pending` if
 * any append is still queued (the tile shows the connectivity-aware sync
 * badge). The two sets are disjoint. See [[append-photo-outbox]].
 */
export function usePendingAppendStatus(projectId: string): {
  pendingRecordIds: Set<string>;
  failedRecordIds: Set<string>;
} {
  const { data: appends } = useQuery({
    queryKey: pendingAppendsByProjectKey(projectId),
    queryFn: () => getPendingAppendsByProject(projectId),
    staleTime: Infinity,
    gcTime: 0,
  });

  return useMemo(() => {
    const failedRecordIds = new Set<string>();
    const pendingByRecord = new Set<string>();
    for (const append of appends ?? []) {
      if (append.status === "failed") failedRecordIds.add(append.recordId);
      else if (append.status === "pending")
        pendingByRecord.add(append.recordId);
    }
    // A record with ANY failed append is "failed", not "pending" — drop it
    // from the pending set so the two are disjoint and failed wins.
    const pendingRecordIds = new Set<string>();
    for (const recordId of pendingByRecord) {
      if (!failedRecordIds.has(recordId)) pendingRecordIds.add(recordId);
    }
    return { pendingRecordIds, failedRecordIds };
  }, [appends]);
}
